import { difference, update } from 'ramda'
import buildWmsLayer from '~/lib/build-wms-layer'
import buildMapboxLayer from '~/lib/build-mapbox-layer'
import addFilterAttributesToLayer from '~/lib/add-filter-attributes-to-layer'
import { getMapServicesCapabilities, getLayerProperties } from '~/lib/get-capabilities'
import { NETHERLANDS_MAP_CENTER, NETHERLANDS_MAP_ZOOM } from '~/lib/constants'
import buildWmtsVectorLayer from '~/lib/build-wmts-vector-layer'


export default {
  namespaced: true,

  state: () => ({
    mapLoaded: false,
    activeFlattenedLayers: [],
    drawMode: null,
    drawnFeatures: [],
    filteredLayerId: null, // id of active layer to filter
    mapboxLayers: [],
    wmsApiLayer: null,
    selectedLayerForSelection: null,
    mapCenter: NETHERLANDS_MAP_CENTER,
    mapZoom: NETHERLANDS_MAP_ZOOM,
    zoomExtent: [],
    multipleSelection: false,
  }),

  getters: {
    mapLoaded: state => state.mapLoaded,
    activeFlattenedLayers: state => (state.mapLoaded && state.activeFlattenedLayers) || [],
    activeFlattenedLayerIds: state => (state.activeFlattenedLayers || []).map(({ id }) => id),
    activeFlattenedLayersIdsWithTimeOption (state, getters) {
      if (!getters.activeFlattenedLayers) {
        return []
      }
      return getters.activeFlattenedLayers.filter(layer => layer?.timeFilter).map(({ id })=>id)
    },
    mapboxLayers(state) {
      if (!state.mapLoaded) {
        return []
      }
      return state.mapboxLayers
    } ,
    wmsApiLayer(state) {
      if (!state.mapLoaded) {
        return []
      }
      return state.wmsApiLayer
    },
    wmsLayerIds: state => (state.activeFlattenedLayers || []).map(({ id }) => id),
    drawMode: state => state.drawMode,
    drawnFeatures: state => state.drawnFeatures,
    filteredLayerId: state => state.filteredLayerId,
    selectedLayerForSelection: state => state.selectedLayerForSelection,
    mapCenter: state => state.mapCenter,
    mapZoom: state => state.mapZoom,
    zoomExtent: state => state.zoomExtent,
    multipleSelection: state => state.multipleSelection,
  },

  mutations: {
    SET_MAP_LOADED(state) {
      state.mapLoaded = true
    },
    SET_RASTER_LAYERS(state, { layers }) {
      state.activeFlattenedLayers = layers
    },
    ADD_ACTIVE_FLATTENED_LAYER(state, layer) {
      state.activeFlattenedLayers = [ layer, ...state.activeFlattenedLayers ]
    },
    MOVE_ACTIVE_FLATTENED_LAYER(state, { fromIndex, toIndex }) {
      var element = state.activeFlattenedLayers[fromIndex]
      state.activeFlattenedLayers.splice(fromIndex, 1)
      state.activeFlattenedLayers.splice(toIndex, 0, element)
    },
    MOVE_MAPBOX_LAYER(state, { fromIndex, toIndex }) {
      var element = state.mapboxLayers[fromIndex]
      state.mapboxLayers.splice(fromIndex, 1)
      state.mapboxLayers.splice(toIndex, 0, element)
    },
    REMOVE_ACTIVE_FLATTENED_LAYER(state, { layer }) {
      state.activeFlattenedLayers = state.activeFlattenedLayers.filter(activeLayer => activeLayer.id !== layer.id)
    },
    ADD_MAPBOX_LAYER(state, layer) {
      state.mapboxLayers = [ layer, ...state.mapboxLayers ]
    },
    REMOVE_MAPBOX_LAYER(state, layerId) {
      state.mapboxLayers = state.mapboxLayers.filter(wmsLayer => wmsLayer.id !== layerId)
    },

    ADD_WMS_API_LAYER(state, layer) {
      state.wmsApiLayer = layer
    },
    REMOVE_WMS_API_LAYER(state) {
      state.wmsApiLayer = null
    },

    RESET_ACTIVE_FLATTENED_LAYERS(state) {
      state.activeFlattenedLayers = []
    },
    RESET_MAPBOX_LAYERS(state) {
      state.mapboxLayers = []
    },
    SET_DRAW_MODE(state, { mode }) {
      state.drawMode = mode
    },
    ADD_DRAWN_FEATURE(state, feature) {
      if (state.multipleSelection) {
        if (!state.drawnFeatures.find(f => f.id === feature.id)) {
          state.drawnFeatures = [
            ...state.drawnFeatures,
            feature,
          ]
        }
      } else {
        state.drawnFeatures = [ feature ]
      }
      
    },
    REMOVE_DRAWN_FEATURE(state, feature) {
      if (state.multipleSelection) {
        state.drawnFeatures = state.drawnFeatures.filter(f => f.id !== feature.id)
      } else {
        state.drawnFeatures = []
      }
      
    },
    SET_DRAWN_FEATURES(state, feature) {
      state.drawnFeatures = Object.freeze(feature)
    },
    UPDATE_MAPBOX_LAYER_OPACITY(state, { id, opacity }) {
      const layerToUpdate = state.mapboxLayers.find(layer => layer.id === id)
      const index = state.mapboxLayers.findIndex(layer => layer.id === id)

      if (!layerToUpdate) {
        return
      }

      layerToUpdate.opacity = opacity
      state.mapboxLayers = update(index, layerToUpdate, state.mapboxLayers)
    },
    SET_FILTERED_LAYER_ID(state, id) {
      state.filteredLayerId = id
    },
    REMOVE_FILTERED_LAYER_ID(state) {
      state.filteredLayerId = null
    },
    SET_SELECTED_LAYER_FOR_SELECTION(state, layer) {
      state.selectedLayerForSelection = layer
    },
    SET_MAP_CENTER(state, coords) {
      state.mapCenter = coords
    },
    SET_MAP_ZOOM(state, zoom) {
      state.mapZoom = zoom
    },
    UPDATE_ZOOM_EXTENT(state, bbox) {
      state.zoomExtent = bbox
    },
    SET_MULTIPLE_SELECTION(state, boolean) {
      state.multipleSelection = boolean
    },
  },

  actions: {
    setMapLoaded({ commit }) {
      commit('SET_MAP_LOADED')
    },
    loadLayerOnMap({ commit, state }, { layers }) {
    
      const layersToAdd = difference(layers, state.activeFlattenedLayers)

      layersToAdd.forEach((layer) => {
        getMapServicesCapabilities(layer.url)
          .then(capabilities => {
            // For ESRI layers, capabilities will be null, so we pass it through
            return getLayerProperties(capabilities, layer)
          })
          .then((properties) => {
            commit('ADD_ACTIVE_FLATTENED_LAYER', { ...layer, ...properties } )
            //wms and wmts raster layers are using the buildMapboxLayer 
            commit('ADD_MAPBOX_LAYER',buildMapboxLayer({ ...layer, ...properties }))
            if (properties.featureType) {
              // if in the properties there is a featureType then we have a vector tiled layer. 
              commit('ADD_MAPBOX_LAYER', buildWmtsVectorLayer({ ...layer, ...properties, id: `${ layer.id }-vector` }))
            }
          },
          )
      })
    },

    reloadLayerOnMap({ commit, state, rootState }) {
      /* If a layer has the time option true,
      then in the filter tab the user can load on
      the map the layer with a new time or a new cql filter */

      const { filteredLayerId, activeFlattenedLayers } = state
      commit('REMOVE_MAPBOX_LAYER', filteredLayerId)
      const { selectedTimestamp, cqlFilter } = rootState.data
      const layer = addFilterAttributesToLayer(activeFlattenedLayers, filteredLayerId, selectedTimestamp, cqlFilter )

      commit('ADD_MAPBOX_LAYER', buildWmsLayer(layer))
    },
    removeLayerFromMap({ commit }, { layers }) {
      layers.forEach(layer => {
        commit('REMOVE_ACTIVE_FLATTENED_LAYER', { layer })
        commit('REMOVE_MAPBOX_LAYER', layer.id)
        commit('REMOVE_MAPBOX_LAYER', layer.layer.split(':')[1])
      })
    },
  
    loadApiLayerOnMap({ commit }, layer) {
      getMapServicesCapabilities(layer.url)
      .then(capabilities => {
        // For ESRI layers, capabilities will be null, so we pass it through
        return getLayerProperties(capabilities, layer)
      })
      .then((properties) => {
        commit('ADD_WMS_API_LAYER', buildMapboxLayer({ ...layer, ...properties }))
      },
      )
    },
    removeApiLayerFromMap({ commit }) {
      commit('REMOVE_WMS_API_LAYER')
    },
    removeFilteredLayerId({ commit }) {
      commit('REMOVE_FILTERED_LAYER_ID')
    },

    resetLayers({ commit }) {
      commit('RESET_ACTIVE_FLATTENED_LAYERS')
      commit('RESET_MAPBOX_LAYERS')
      commit('REMOVE_FILTERED_LAYER_ID')
    },

    moveRasterLayer({ commit }, { fromIndex, toIndex }) {
      commit('MOVE_ACTIVE_FLATTENED_LAYER', { fromIndex, toIndex })
      commit('MOVE_MAPBOX_LAYER', { fromIndex, toIndex })
    },

    setDrawMode({ commit, state }, { mode }) {
      const modeToCommit = state.drawMode === mode ? null : mode
      commit('SET_DRAW_MODE', { mode: modeToCommit })
    },

    addDrawnFeature({ commit }, feature) {
      commit('ADD_DRAWN_FEATURE', feature)
    },

    removeDrawnFeature({ commit }, feature) {
      commit('REMOVE_DRAWN_FEATURE', feature)
    },

    setDrawnFeatures({ commit }, features) {
      commit('SET_DRAWN_FEATURES', features)
    },

    clearDrawnFeatures({ commit }) {
      commit('SET_DRAWN_FEATURES', [])
    },

    updateWmsLayerOpacity({ commit }, { id, opacity }) {
      commit('UPDATE_MAPBOX_LAYER_OPACITY', { id, opacity })
    },

    setFilteredLayerId({ commit }, id) {
      commit('SET_FILTERED_LAYER_ID', id)
    },
    setSelectedLayerForSelection({ commit }, layer) {
      commit('SET_SELECTED_LAYER_FOR_SELECTION', layer)
    },
    setMapCenter({ commit }, coords) {
      commit('SET_MAP_CENTER', coords)
    },
    setMapZoom({ commit }, zoom) {
      commit('SET_MAP_ZOOM', zoom)
    },
    updateZoomExtent({ commit, state }, id) {
      //updates zoom extent based on the bbox of the layer.
      //input is the id of the layer
      //activeFlattenedLayers
      const layerToZoom = state.activeFlattenedLayers.find(layer => layer.id === id)
      commit('UPDATE_ZOOM_EXTENT', layerToZoom.bbox)
    },
    setMultipleSelection({ commit }, boolean) {
      commit('SET_MULTIPLE_SELECTION', boolean)
    },
  },
}
